import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { format, isToday, isTomorrow, isSameDay, startOfDay, addDays, parseISO, isAfter, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import tankerkoenigService from '../services/tankerkoenigService';

const { 
  FiCalendar, FiCheckSquare, FiShoppingCart, FiCoffee, 
  FiHeart, FiArrowRight, FiDroplet, FiRefreshCw, FiHome, FiTrash2 
} = FiIcons;

// Helper to safely parse any date input
const ensureDate = (dateInput) => {
  if (!dateInput) return null;
  const date = typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
  return isValid(date) ? date : null;
};

export default function EnhancedDashboard() {
  const { state } = useFamilyContext();
  const { user, familyProfile } = useAuth();
  const [fuelData, setFuelData] = useState([]);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [fuelConfig, setFuelConfig] = useState(null);
  
  const fetchFuel = useCallback(async (config) => {
    if (!config?.enabled || !config?.apiKey) return;
    setFuelLoading(true);
    try {
      const stations = await tankerkoenigService.getNearbyStations(
        config.lat, config.lng, config.radius, config.fuelType || 'all', config.apiKey
      );
      if (stations && Array.isArray(stations)) {
        const subscribedIds = config.subscribedStations || [];
        const filtered = subscribedIds.length > 0 
          ? stations.filter(s => subscribedIds.includes(s.id))
          : stations.slice(0, 3);
        setFuelData(filtered);
      }
    } catch (err) {
      console.error("Dashboard: Fuel fetch error", err);
    } finally {
      setFuelLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      if (!familyProfile?.family_id) return;
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'tankerkoenig_config')
        .eq('family_id', familyProfile.family_id)
        .maybeSingle();
      
      if (data?.value?.enabled) {
        setFuelConfig(data.value);
        fetchFuel(data.value);
      }
    };
    loadConfig();
  }, [familyProfile?.family_id, fetchFuel]);

  const upcomingEvents = useMemo(() => {
    const today = startOfDay(new Date());
    
    const appointments = (state.appointments || []).map(a => ({
      ...a, date: ensureDate(a.date), icon: FiCalendar, color: 'text-blue-500', bg: 'bg-blue-50', category: 'Termin'
    })).filter(a => a.date);

    const events = (state.events || []).map(e => ({
      ...e, date: ensureDate(e.date), icon: FiCalendar, color: 'text-purple-500', bg: 'bg-purple-50', category: 'Event'
    })).filter(e => e.date);

    const household = (state.householdPlans || [])
      .filter(p => p.due_date && !p.completed)
      .map(p => ({
        ...p, date: ensureDate(p.due_date), icon: FiHome, color: 'text-green-500', bg: 'bg-green-50', category: 'Haushalt'
      })).filter(p => p.date);

    const trash = (state.trashCalendar || []).map(t => ({
      ...t, date: ensureDate(t.date), title: `Abfall: ${t.type}`, icon: FiTrash2, color: 'text-orange-500', bg: 'bg-orange-50', category: 'Müll'
    })).filter(t => t.date);

    const all = [...appointments, ...events, ...household, ...trash]
      .filter(item => isAfter(item.date, today) || isSameDay(item.date, today))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return {
      today: all.filter(item => isToday(item.date)),
      tomorrow: all.filter(item => isTomorrow(item.date)),
      next: all.filter(item => !isToday(item.date) && !isTomorrow(item.date)).slice(0, 4)
    };
  }, [state]);

  const stats = [
    { label: 'Termine heute', value: upcomingEvents.today.length, icon: FiCalendar, color: 'blue', path: '/calendar' },
    { label: 'Offene Aufgaben', value: (state.todos || []).filter(t => !t.completed).length, icon: FiCheckSquare, color: 'green', path: '/todos' },
    { label: 'Einkaufsliste', value: (state.shoppingItems || []).filter(i => !i.completed).length, icon: FiShoppingCart, color: 'orange', path: '/shopping' },
    { label: 'Mahlzeiten geplant', value: (state.mealPlans || []).length, icon: FiCoffee, color: 'purple', path: '/meals' },
  ];

  const todayStr = useMemo(() => {
    try {
      return format(new Date(), 'EEEE, do MMMM yyyy', { locale: de });
    } catch (e) {
      return '';
    }
  }, []);

  return (
    <div className="space-y-8 pb-12">
      <header>
        <h1 className="text-3xl font-bold dark:text-white transition-colors">Hallo, {user?.user_metadata?.full_name || 'Familie'}! 👋</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{todayStr}</p>
      </header>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <Link key={idx} to={stat.path} className="block group">
            <motion.div
              whileHover={{ y: -4, scale: 1.02 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 transition-all group-hover:shadow-md"
            >
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-50 dark:bg-${stat.color}-900/20 flex items-center justify-center text-${stat.color}-600 mb-4 transition-colors`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.label}</p>
              <p className="text-2xl font-bold dark:text-white mt-1">{stat.value}</p>
            </motion.div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-bold dark:text-white flex items-center">
                <SafeIcon icon={FiCalendar} className="mr-2 text-blue-500" />
                Anstehende Termine
              </h2>
              <Link to="/calendar" className="text-sm text-blue-600 font-semibold hover:underline flex items-center">
                Kalender öffnen <SafeIcon icon={FiArrowRight} className="ml-1" />
              </Link>
            </div>
            
            <div className="p-6 space-y-8">
              {upcomingEvents.today.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4">Heute</h3>
                  <div className="space-y-3">
                    {upcomingEvents.today.map((item, i) => <EventCard key={i} item={item} />)}
                  </div>
                </div>
              )}

              {upcomingEvents.tomorrow.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-[0.2em] mb-4">Morgen</h3>
                  <div className="space-y-3">
                    {upcomingEvents.tomorrow.map((item, i) => <EventCard key={i} item={item} />)}
                  </div>
                </div>
              )}

              {upcomingEvents.next.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Demnächst</h3>
                  <div className="space-y-3">
                    {upcomingEvents.next.map((item, i) => <EventCard key={i} item={item} />)}
                  </div>
                </div>
              )}

              {upcomingEvents.today.length === 0 && upcomingEvents.tomorrow.length === 0 && upcomingEvents.next.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiCalendar} className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">Keine anstehenden Termine gefunden.</p>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="space-y-6">
          {fuelConfig?.enabled && (
            <section className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <h2 className="text-lg font-bold dark:text-white flex items-center">
                  <SafeIcon icon={FiDroplet} className="mr-2 text-blue-500" />
                  Spritpreise
                </h2>
                <button 
                  onClick={() => fetchFuel(fuelConfig)}
                  disabled={fuelLoading}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${fuelLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {fuelData.length > 0 ? fuelData.map((station) => (
                  <div key={station.id} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-sm dark:text-white truncate pr-2">{station.brand || station.name}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${station.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {station.isOpen ? 'OFFEN' : 'ZU'}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-black text-blue-600 dark:text-blue-400">
                      <span className="text-xs self-center text-gray-400">{(fuelConfig.fuelType || 'e5').toUpperCase()}</span>
                      <span>{station.price?.toFixed(3)} €</span>
                    </div>
                  </div>
                )) : (
                  <p className="text-center py-4 text-xs text-gray-500">Keine Stationen konfiguriert.</p>
                )}
              </div>
            </section>
          )}

          <section className="bg-gradient-to-br from-pink-500 to-rose-600 p-6 rounded-2xl shadow-lg text-white">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                <SafeIcon icon={FiHeart} className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-bold">Familien-Liebe</h2>
            </div>
            <p className="text-sm text-pink-50 opacity-90 leading-relaxed mb-4">
              Sende heute jemandem ein Lächeln oder einen virtuellen Kuss!
            </p>
            <Link to="/dashboard" className="block w-full py-2.5 bg-white text-pink-600 rounded-xl font-bold text-sm shadow-sm hover:bg-pink-50 transition-all text-center">
              Kuss senden
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}

function EventCard({ item }) {
  const dateFormatted = useMemo(() => {
    if (!item.date || !isValid(item.date)) return '--.--.';
    return format(item.date, 'dd.MM.');
  }, [item.date]);

  return (
    <div className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-xl transition-all group border border-transparent hover:border-gray-100 dark:hover:border-gray-700">
      <div className={`w-10 h-10 ${item.bg || 'bg-gray-50'} dark:bg-gray-800 rounded-lg flex items-center justify-center ${item.color || 'text-gray-500'} mr-4 transition-transform group-hover:scale-110`}>
        <SafeIcon icon={item.icon} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold dark:text-white truncate">{item.title}</h4>
          <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase">{item.category}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {item.time ? `${item.time} Uhr` : 'Ganztägig'} {item.location && `• ${item.location}`}
        </p>
      </div>
      <div className="ml-4 text-[11px] font-bold text-gray-400 group-hover:text-blue-500 transition-colors bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
        {dateFormatted}
      </div>
    </div>
  );
}